import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { MdClose, MdVerifiedUser, MdLocationOn, MdPayment, MdCheckCircle, MdChevronRight, MdEdit } from 'react-icons/md';
import { FaTruck, FaShieldAlt, FaPlus, FaMinus } from 'react-icons/fa';
import Context from '../context';
import displayINRCurrency from '../helpers/displayCurrency';
import SummaryApi from '../common';
import service from '../common/HttpService';
import { toast } from 'react-toastify';
import SavedAddressDrawer from './SavedAddressDrawer';
import { getCookie } from '../common/cookieHelper';
import useTranslate from '../hooks/useTranslate';
import { trackLead } from '../helpers/analyticsHelper';

// const RAZORPAY_KEY = 'rzp_test_HkzyriLfjgebbp';
const RAZORPAY_KEY = 'rzp_live_SxvDQfGggybL8S';


const loadRazorpayScript = () => {
    return new Promise((resolve) => {
        if (window.Razorpay) {
            resolve(true);
            return;
        }
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
};

const CheckoutSlider = ({ onClose }) => {
    const { checkoutData, setCheckoutData, cartItems, setCartItems, setCartProductCount, user, fetchUserAddToCart, languageCode, translateText } = useContext(Context);
    const [step, setStep] = useState(1); // 1: Order Summary, 2: Address, 3: Payment, 4: Success
    const [loading, setLoading] = useState(false);

    const [items, setItems] = useState([]);
    const [cartSummary, setCartSummary] = useState(null);
    const [translatedItems, setTranslatedItems] = useState([]);
    const [addresses, setAddresses] = useState([]);
    const [showAddAddress, setShowAddAddress] = useState(false);
    const [showSavedAddresses, setShowSavedAddresses] = useState(false);

    const t = useTranslate({
        checkout: "Checkout",
        stepCount: "Step %step% of 3",
        done: "Done",
        orderConfirmed: "Order Confirmed!",
        harvestPrepared: "Your harvest is being prepared for delivery.",
        arrivingBy: "Arriving By",
        placeOrder: "Place Order",
        continue: "Continue",
        continueToPayment: "Continue to Payment",
        payAndPlaceOrder: "Pay & Place Order",
        confirmOrder: "Confirm & Place Order",
        adding: "Adding...",
        saveAddress: "Save Address",
        reviewOrder: "Review Order",
        harvest: "Harvest",
        product: "Product",
        itemsTotal: "Items Total",
        savings: "Savings",
        shipping: "Shipping",
        free: "FREE",
        amountPayable: "Amount Payable",
        qualityAssured: "Quality Assured. Your order is secured with FasalShop Guarantee.",
        selectLocation: "Select Delivery Location",
        selectPaymentMethod: "Select Payment Method",
        onlinePayment: "Online Payment (UPI / Cards / NetBanking)",
        onlineDesc: "Fast & secure payment gateway by Razorpay",
        codPayment: "Cash on Delivery (COD)",
        codDesc: "Pay with cash when your harvest is delivered",
        selected: "Selected",
        noName: "No Name",
        home: "Home",
        farm: "Farm",
        office: "Office",
        noAddress: "No Address",
        near: "Near",
        city: "City",
        state: "State",
        noPhone: "No Phone",
        addNewAddress: "Add New Address",
        name: "Name",
        phone: "Phone",
        completeAddress: "Complete Address",
        pincode: "Pincode",
        landmark: "Landmark (Optional)",
        choosePayment: "Choose Payment",
        cod: "Cash on Delivery",
        upi: "UPI Payment",
        card: "Credit / Debit Card",
        verifiedCustomer: "Verified Customer",
        loginAddAddress: "Please login to add address",
        successfulAddressLink: "Address added successfully",
        failedAddressLink: "Failed to add address",
        errorAddAddress: "Error adding address",
        loginPlaceOrder: "Please login to place your order",
        orderId: "Order ID",
        emptyCart: "Your cart is empty",
        noItemsInCart: "No items found in your checkout order.",
        continueShopping: "Continue Shopping"
    });

    const [newAddress, setNewAddress] = useState({
        name: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        landmark: '',
        pincode: '',
        type: t.home
    });

    useEffect(() => {
        window.__activeModals = (window.__activeModals || 0) + 1;
        document.body.style.overflow = 'hidden';
        return () => {
            window.__activeModals = (window.__activeModals || 0) - 1;
            if (window.__activeModals <= 0) {
                document.body.style.overflow = 'unset';
                window.__activeModals = 0;
            }
        };
    }, []);

    const fetchCartSummary = useCallback(async () => {
        try {
            const response = await service.get(SummaryApi.view_cart.url);
            if (response && (response.success === "true" || response.success === true)) {
                setCartSummary(response.extraData || null);
            }
        } catch (err) {
            console.error("Checkout fetch cart summary error:", err);
        }
    }, []);

    useEffect(() => {
        fetchCartSummary();
    }, [fetchCartSummary]);

    useEffect(() => {
        const translateCheckoutItems = async () => {
            if (!items.length || !languageCode || languageCode === 'en') {
                setTranslatedItems(items);
                return;
            }

            try {
                const translated = await Promise.all(items.map(async (item) => {
                    const p = (typeof item.productId === 'object' ? item.productId : null) || item.product || {};
                    const rate = item.selectedRate || (item.item_rate && item.item_rate[0]) || (item.item_rates && item.item_rates[0]) || {};
                    const pName = item.itemname || rate.itemname || p.itemname || p.productName || "Product";
                    const subName = p.subcategory_name || p.category_name || t.harvest;

                    const [tName, tSub] = await Promise.all([
                        translateText(pName, languageCode),
                        translateText(subName, languageCode)
                    ]);

                    return {
                        ...item,
                        translatedName: tName,
                        translatedSub: tSub
                    };
                }));
                setTranslatedItems(translated);
            } catch (error) {
                console.error("Checkout translation error:", error);
                setTranslatedItems(items);
            }
        };
        translateCheckoutItems();
    }, [items, languageCode, translateText, t.harvest]);

    useEffect(() => {
        // Only run sync if moving away/towards cartItems or if checkoutData changes
        // But be careful not to overwrite local quantity/rate choices during active editing
        setItems(prevItems => {
            const nextItems = checkoutData ? (() => {
                const existing = prevItems.find(it => (it.id || it._id || it.cart_id) === (checkoutData.id || checkoutData._id || checkoutData.cart_id)) || prevItems[0];
                const p = (typeof checkoutData.productId === 'object' ? checkoutData.productId : null) || checkoutData.product || {};
                const allRates = (Array.isArray(p.item_rates) && p.item_rates.length > 0 ? p.item_rates : (Array.isArray(p.item_rate) && p.item_rate.length > 0 ? p.item_rate : (p.rates || []))) ||
                    (checkoutData.item_rates || checkoutData.item_rate || checkoutData.rates || []);
                let foundRate = existing?.selectedRate || checkoutData.selectedRate || allRates.find(r => String(r.id || r._id) === String(checkoutData.rate_id)) || allRates[0] || null;

                // Look up matching cart item from cartItems or cartSummary for backend shipping rate
                const curProdId = String(p._id || p.id || checkoutData.product_id || "");
                const cartMatch = cartItems?.find(it => {
                    const itProdId = String(it.productId?._id || it.productId?.id || it.product_id || it.productId || "");
                    return itProdId === curProdId && itProdId !== "";
                }) || cartSummary?.cart?.find(it => {
                    const itProdId = String(it.product_id || it.productId?._id || it.productId?.id || "");
                    return itProdId === curProdId && itProdId !== "";
                });

                const cartMatchRate = (cartMatch?.item_rate && cartMatch.item_rate[0]) || (cartMatch?.item_rates && cartMatch.item_rates[0]) || cartMatch?.selectedRate;
                const resolvedShipping = checkoutData.shipping_charges || checkoutData.shipping_charge ||
                    foundRate?.shipping_charges || foundRate?.shipping_charge ||
                    cartMatchRate?.shipping_charges || cartMatchRate?.shipping_charge ||
                    cartMatch?.shipping_charges || cartMatch?.shipping_charge ||
                    cartSummary?.shipping_charge || p.shipping_charges || p.shipping_charge || 0;

                if (foundRate) {
                    foundRate = {
                        ...foundRate,
                        shipping_charges: foundRate.shipping_charges || resolvedShipping,
                        shipping_charge: foundRate.shipping_charge || resolvedShipping
                    };
                }

                const singleItem = {
                    ...checkoutData,
                    productId: p,
                    selectedRate: foundRate,
                    shipping_charges: resolvedShipping,
                    shipping_charge: resolvedShipping,
                    quantity: existing?.quantity || Number(checkoutData.quantity || checkoutData.qty) || 1
                };

                // Check for other cart items if they exist
                const sourceCart = (Array.isArray(cartItems) && cartItems.length > 0)
                    ? cartItems
                    : (Array.isArray(cartSummary?.cart) ? cartSummary.cart : []);

                const otherCartItems = sourceCart.filter(item => {
                    const itP = (typeof item.productId === 'object' ? item.productId : null) || item.product || {};
                    const itProdId = String(itP._id || itP.id || item.product_id || item.productId || "");
                    return itProdId !== curProdId && itProdId !== "";
                });

                if (otherCartItems.length > 0) {
                    const mappedOther = otherCartItems.map(item => {
                        const itemP = (typeof item.productId === 'object' ? item.productId : null) || item.product || {};
                        const itemRates = (item.item_rates && item.item_rates.length > 0 ? item.item_rates : (item.item_rate && item.item_rate.length > 0 ? item.item_rate : [])) || itemP.item_rates || itemP.rates || [];
                        const existingOther = prevItems.find(it => (it.id || it._id || it.cart_id) === (item.id || item._id || item.cart_id));
                        const itemFoundRate = existingOther?.selectedRate || (item.item_rate && item.item_rate[0]) || (item.item_rates && item.item_rates[0]) || itemRates.find(r => String(r.id || r._id) === String(item.rate_id || item.item_rate_id)) || itemRates[0] || null;
                        return {
                            ...item,
                            productId: itemP,
                            selectedRate: itemFoundRate,
                            quantity: existingOther?.quantity || Number(item.quantity || item.qty) || 1
                        };
                    });
                    return [singleItem, ...mappedOther];
                }

                return [singleItem];
            })() : cartItems.map(item => {
                const p = (typeof item.productId === 'object' ? item.productId : null) || item.product || {};
                const allRates = (item.item_rates && item.item_rates.length > 0 ? item.item_rates : (item.item_rate && item.item_rate.length > 0 ? item.item_rate : [])) || p.item_rates || p.rates || [];
                // Look for existing local item to preserve its selectedRate if possible
                const existing = prevItems.find(it => (it.id || it._id || it.cart_id) === (item.id || item._id || item.cart_id));
                const foundRate = existing?.selectedRate || (item.item_rate && item.item_rate[0]) || (item.item_rates && item.item_rates[0]) || allRates.find(r => String(r.id || r._id) === String(item.rate_id || item.item_rate_id)) || allRates[0] || null;
                return {
                    ...item,
                    productId: p,
                    selectedRate: foundRate,
                    quantity: existing?.quantity || Number(item.quantity || item.qty) || 1
                };
            });
            return nextItems;
        });
    }, [checkoutData, cartItems, cartSummary]);

    const fetchAddresses = async () => {
        try {
            const response = await service.get(SummaryApi.getAllAddress.url);
            if (response.success === "true" || response.success === true) {
                const data = response.extraData;
                const addrList = data?.address && Array.isArray(data.address)
                    ? data.address
                    : (Array.isArray(data) ? data : (data ? [data] : []));
                setAddresses(addrList);
                const defaultAddr = addrList.find(a => a.is_default === "true" || a.selected || a.default_address === "1" || a.default_address === true);
                if (defaultAddr) setSelectedAddress(defaultAddr.address_id || defaultAddr.id || defaultAddr._id);
            }
        } catch (error) {
            console.error("Error fetching addresses:", error);
        }
    };

    useEffect(() => {
        if (step === 2) {
            fetchAddresses();
        }
    }, [step]);

    const [selectedAddress, setSelectedAddress] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('cod');

    const handleAddAddress = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const userId = user?._id || user?.id || getCookie('user_id') || getCookie('userId');
            if (!userId) {
                toast.error(t.loginAddAddress);
                setLoading(false);
                return;
            }

            const payload = {
                user_id: userId,
                name: newAddress.name,
                address: newAddress.address,
                city: newAddress.city,
                state: newAddress.state,
                landmark: newAddress.landmark,
                pin: newAddress.pincode,
                mobile: newAddress.phone,
                type: newAddress.type
            };

            // Mobile validation: exactly 10 digits
            if (!/^\d{10}$/.test(payload.mobile)) {
                toast.error("Please enter a valid 10-digit phone number");
                setLoading(false);
                return;
            }

            // Pincode validation: exactly 6 digits
            if (!/^\d{6}$/.test(payload.pin)) {
                toast.error("Please enter a valid 6-digit Pincode");
                setLoading(false);
                return;
            }

            const response = await service.post(SummaryApi.addShippingAddress.url, payload);
            if (response.success === "true" || response.success === true) {
                toast.success(response.message || t.successfulAddressLink);
                setShowAddAddress(false);
                fetchAddresses();
            } else {
                toast.error(response.message || t.failedAddressLink);
            }
        } catch (error) {
            toast.error(t.errorAddAddress);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectAddress = async (id) => {
        setSelectedAddress(id);
        try {
            const userId = user?._id || user?.id || getCookie('user_id') || getCookie('userId');
            const response = await service.post(SummaryApi.setDefaultShippingAddress.url, {
                address_id: id,
                user_id: userId
            });
            if (response.success === "true" || response.success === true) {
                fetchAddresses(); // Refresh list to get updated default flags from server
            }
        } catch (error) {
            console.error("Error setting default address:", error);
        }
    };

    const deleteItem = async (idx) => {
        const item = items[idx];
        if (!item) return;

        const curProdId = String(item.productId?._id || item.productId?.id || item.product_id || item.productId || "");
        const cartMatch = cartItems?.find(it => {
            const itProdId = String(it.productId?._id || it.productId?.id || it.product_id || it.productId || "");
            return itProdId === curProdId && itProdId !== "";
        }) || cartSummary?.cart?.find(it => {
            const itProdId = String(it.product_id || it.productId?._id || it.productId?.id || "");
            return itProdId === curProdId && itProdId !== "";
        });

        const cartId = (item.cart_id && !isNaN(Number(item.cart_id)))
            ? item.cart_id
            : ((item.id && !isNaN(Number(item.id)))
                ? item.id
                : (cartMatch?.cart_id || cartMatch?.id || item.cart_id || item.id || item._id));

        // Optimistic UI updates
        setItems(prev => prev.filter((_, i) => i !== idx));
        setTranslatedItems(prev => prev.filter((_, i) => i !== idx));

        if (checkoutData) {
            setCheckoutData(null);
        }

        // Optimistically update cartSummary
        setCartSummary(prev => {
            if (!prev) return { cart: [], cart_total: 0, total_mrp: 0, discount: 0, shipping_charge: 0 };
            const currentList = Array.isArray(prev.cart) ? prev.cart : (Array.isArray(prev) ? prev : []);
            const updated = currentList.filter(it => {
                const itId = (it.cart_id && !isNaN(Number(it.cart_id))) ? it.cart_id : ((it.id && !isNaN(Number(it.id))) ? it.id : (it.id || it.cart_id || it._id));
                return String(itId) !== String(cartId);
            });
            return Array.isArray(prev) ? updated : { ...prev, cart: updated };
        });

        if (typeof setCartItems === 'function') {
            setCartItems(prev => {
                if (!Array.isArray(prev)) return [];
                return prev.filter(it => {
                    const itId = (it.cart_id && !isNaN(Number(it.cart_id))) ? it.cart_id : ((it.id && !isNaN(Number(it.id))) ? it.id : (it.id || it.cart_id || it._id));
                    return String(itId) !== String(cartId);
                });
            });
        }
        if (typeof setCartProductCount === 'function') {
            setCartProductCount(prev => Math.max(0, (Number(prev) || 1) - 1));
        }

        const userId = user?._id || user?.id || user?.user_id || getCookie('user_id') || localStorage.getItem('user_id');

        if (cartId) {
            try {
                await service.post(SummaryApi.delete_cart.url, {
                    id: String(cartId),
                    user_id: userId
                });
                if (typeof fetchUserAddToCart === 'function') {
                    await fetchUserAddToCart();
                }
                fetchCartSummary();
            } catch (error) {
                console.error("Error deleting cart item:", error);
            }
        }
    };

    const updateItemQuantity = async (idx, change) => {
        const item = items[idx];
        if (!item) return;

        const currentQty = Number(item.quantity || item.qty || 1);
        const newQty = currentQty + change;

        if (newQty <= 0) {
            await deleteItem(idx);
            return;
        }

        // Optimistic UI update
        setItems(prev => prev.map((it, i) =>
            i === idx ? { ...it, quantity: newQty, qty: newQty } : it
        ));
        setTranslatedItems(prev => prev.map((it, i) =>
            i === idx ? { ...it, quantity: newQty, qty: newQty } : it
        ));

        if (checkoutData && items.length === 1) {
            setCheckoutData(prev => prev ? ({ ...prev, quantity: newQty, qty: newQty }) : null);
        }

        const curProdId = String(item.productId?._id || item.productId?.id || item.product_id || item.productId || "");
        const cartMatch = cartItems?.find(it => {
            const itProdId = String(it.productId?._id || it.productId?.id || it.product_id || it.productId || "");
            return itProdId === curProdId && itProdId !== "";
        }) || cartSummary?.cart?.find(it => {
            const itProdId = String(it.product_id || it.productId?._id || it.productId?.id || "");
            return itProdId === curProdId && itProdId !== "";
        });

        const cartId = (item.cart_id && !isNaN(Number(item.cart_id)))
            ? item.cart_id
            : ((item.id && !isNaN(Number(item.id)))
                ? item.id
                : (cartMatch?.cart_id || cartMatch?.id || item.cart_id || item.id || item._id));

        // Optimistically update cartSummary
        setCartSummary(prev => {
            if (!prev) return prev;
            const currentList = Array.isArray(prev.cart) ? prev.cart : (Array.isArray(prev) ? prev : []);
            const updated = currentList.map(it => {
                const itId = (it.cart_id && !isNaN(Number(it.cart_id))) ? it.cart_id : ((it.id && !isNaN(Number(it.id))) ? it.id : (it.id || it.cart_id || it._id));
                return String(itId) === String(cartId) ? { ...it, qty: newQty, quantity: newQty } : it;
            });
            return Array.isArray(prev) ? updated : { ...prev, cart: updated };
        });

        const userId = user?._id || user?.id || user?.user_id || getCookie('user_id') || localStorage.getItem('user_id');

        if (cartId) {
            try {
                await service.post(SummaryApi.update_cart.url, {
                    id: String(cartId),
                    qty: String(newQty),
                    user_id: userId
                });
                if (typeof fetchUserAddToCart === 'function') {
                    await fetchUserAddToCart();
                }
                fetchCartSummary();
            } catch (error) {
                console.error("Error updating cart quantity:", error);
            }
        }
    };

    const updateItemRate = (itemIdx, rate) => {
        setItems(prev => prev.map((item, i) => {
            if (i === itemIdx) {
                return {
                    ...item,
                    selectedRate: rate,
                    rate_id: rate.id || rate._id,
                    shipping_charges: rate.shipping_charges || rate.shipping_charge || item.shipping_charges,
                    shipping_charge: rate.shipping_charge || rate.shipping_charges || item.shipping_charge
                };
            }
            return item;
        }));
        setTranslatedItems(prev => prev.map((item, i) => {
            if (i === itemIdx) {
                return {
                    ...item,
                    selectedRate: rate,
                    rate_id: rate.id || rate._id,
                    shipping_charges: rate.shipping_charges || rate.shipping_charge || item.shipping_charges,
                    shipping_charge: rate.shipping_charge || rate.shipping_charges || item.shipping_charge
                };
            }
            return item;
        }));
        if (checkoutData && items.length === 1) {
            setCheckoutData(prev => prev ? ({
                ...prev,
                selectedRate: rate,
                rate_id: rate.id || rate._id,
                shipping_charges: rate.shipping_charges || rate.shipping_charge || prev.shipping_charges,
                shipping_charge: rate.shipping_charge || rate.shipping_charges || prev.shipping_charge
            }) : null);
        }
    };

    const parsePrice = (price) => {
        if (!price || price === "0") return 0;
        if (typeof price === 'number') return price;
        if (typeof price === 'string') {
            const cleaned = price.replace(/,/g, '').trim();
            return Number(cleaned) || 0;
        }
        return 0;
    };

    const getProductPrice = (item) => {
        const rate = item?.selectedRate || (item?.item_rate && item?.item_rate?.[0]) || (item?.item_rates && item?.item_rates?.[0]) || {};
        const p = (typeof item?.productId === 'object' ? item.productId : null) || item?.product || {};
        const price = rate.mrp || item?.mrp || p.rates?.[0]?.mrp || p.item_rates?.[0]?.mrp || p.price || p.mrp || 0;
        return parsePrice(price);
    };

    const getProductSellingPrice = (item) => {
        const rate = item?.selectedRate || (item?.item_rate && item?.item_rate?.[0]) || (item?.item_rates && item?.item_rates?.[0]) || {};
        const p = (typeof item?.productId === 'object' ? item.productId : null) || item?.product || {};
        const price = rate.salerate || item?.salerate || p.salerate || p.rates?.[0]?.salerate || p.item_rates?.[0]?.salerate || p.sellingPrice || 0;
        return parsePrice(price);
    };

    const getProductShippingCharge = (item) => {
        const rate = item?.selectedRate || (item?.item_rate && item?.item_rate?.[0]) || (item?.item_rates && item?.item_rates?.[0]) || {};
        const p = (typeof item?.productId === 'object' ? item.productId : null) || item?.product || {};
        let charge = item?.shipping_charges || item?.shipping_charge || item?.delivery_charge || item?.delivery_charges ||
            rate?.shipping_charges || rate?.shipping_charge || rate?.delivery_charge || rate?.delivery_charges ||
            p?.shipping_charges || p?.shipping_charge || p?.delivery_charge || p?.delivery_charges ||
            p?.rates?.[0]?.shipping_charges || p?.item_rates?.[0]?.shipping_charges;

        if (!charge || Number(charge) === 0) {
            const curProdId = String(p._id || p.id || item?.product_id || "");
            const match = cartItems?.find(it => {
                const itProdId = String(it.productId?._id || it.productId?.id || it.product_id || it.productId || "");
                return itProdId === curProdId && itProdId !== "";
            }) || cartSummary?.cart?.find(it => {
                const itProdId = String(it.product_id || it.productId?._id || it.productId?.id || "");
                return itProdId === curProdId && itProdId !== "";
            });
            const matchRate = (match?.item_rate && match.item_rate[0]) || (match?.item_rates && match.item_rates[0]) || match?.selectedRate;
            charge = matchRate?.shipping_charges || matchRate?.shipping_charge || match?.shipping_charges || match?.shipping_charge || (items.length === 1 ? cartSummary?.shipping_charge : 0) || 0;
        }

        return parsePrice(charge);
    };

    const subtotal = items.reduce((acc, curr) => {
        return acc + (((Number(curr.qty || curr.quantity)) || 1) * getProductSellingPrice(curr));
    }, 0);

    const mrpTotal = items.reduce((acc, curr) => {
        return acc + (((Number(curr.qty || curr.quantity)) || 1) * getProductPrice(curr));
    }, 0);

    const computedItemsShipping = items.length === 0 ? 0 : items.reduce((max, curr) => {
        return Math.max(max, getProductShippingCharge(curr));
    }, 0);

    const deliveryCharge = items.length === 0 ? 0 : (computedItemsShipping > 0
        ? computedItemsShipping
        : (cartSummary?.shipping_charge !== undefined && cartSummary?.shipping_charge !== null && Number(cartSummary.shipping_charge) > 0
            ? parsePrice(cartSummary.shipping_charge)
            : 0));

    const discount = mrpTotal > subtotal ? mrpTotal - subtotal : 0;
    const totalAmount = items.length === 0 ? 0 : (subtotal + deliveryCharge);


    const [orderId, setOrderId] = useState(null);

    const handlePlaceOrder = async () => {
        if (!selectedAddress) {
            toast.error("Please select a delivery address");
            setStep(2);
            return;
        }

        setLoading(true);
        try {
            // Get user_id and ensure it is not 'undefined' or 'null' as a string
            let userId = user?._id || user?.id || getCookie('user_id') || getCookie('userId');

            // Convert to string safely
            const finalUserId = userId ? String(userId).trim() : "";

            if (!finalUserId || finalUserId === "undefined" || finalUserId === "null" || finalUserId === "") {
                toast.error(t.loginPlaceOrder);
                setLoading(false);
                return;
            }

            // Format order items with strictly numeric IDs for products/rates (as seen in addToCart)
            const orderItems = items.map(item => {
                const p = item.productId || {};
                const prodId = String(p._id || p.id || "").replace(/[^0-9]/g, '') || "0";
                const selectedR = item.selectedRate || {};
                const rId = String(selectedR.id || selectedR._id || "").replace(/[^0-9]/g, '') || "0";
                const sRate = getProductSellingPrice(item);

                return {
                    product_id: prodId,
                    quantity: String(item.quantity || item.qty || 1),
                    qty: String(item.quantity || item.qty || 1),
                    rate_id: rId,
                    price: String(sRate)
                };
            });

            // Extract vendor_id (numeric)
            const firstProduct = items[0]?.productId || {};
            const vendorId = String(firstProduct.vendor_id || firstProduct.user_id || "1").replace(/[^0-9]/g, '') || "1";

            // Address and User IDs might be Mongo/Hex strings, so we don't strip non-numeric check
            const addrId = String(selectedAddress);
            const userIdToLink = String(finalUserId);

            // COD Flow
            if (paymentMethod === 'cod') {
                const formData = new FormData();
                formData.append('user_id', userIdToLink);
                formData.append('address_id', addrId);
                formData.append('type', (items.length > 1 || !checkoutData) ? "2" : "1"); // 1 = buy_now, 2 = cart
                formData.append('total_cost', String(totalAmount));
                formData.append('vendor_id', vendorId);
                formData.append('items', JSON.stringify(orderItems));
                formData.append('payment_method', '0');
                formData.append('discount', String(discount || 0));
                formData.append('shipping_charge', String(deliveryCharge || 0));

                const responseData = await service.post(SummaryApi.placeOrder.url, formData);

                if (responseData.success === "true" || responseData.success === true) {
                    toast.success(responseData.message || "Order placed successfully!");
                    const finalOrderId = responseData?.extraData?.order_id || responseData?.order_id || `FSL-${Math.floor(100000 + Math.random() * 900000)}`;
                    setOrderId(finalOrderId);
                    setStep(4);
                    trackLead({
                        content_name: 'Order Placement (COD)',
                        value: Number(totalAmount) || 0,
                        currency: 'INR',
                        order_id: finalOrderId
                    });
                    if (typeof fetchUserAddToCart === 'function') {
                        fetchUserAddToCart();
                    }
                } else {
                    console.error("Order failed response:", responseData);
                    toast.error(responseData.message || "Failed to place order. Please try again.");
                }
                setLoading(false);
                return;
            }

            // Online Payment Flow (Razorpay)
            const activeAddress = addresses.find(addr => String(addr.address_id || addr.id || addr._id) === String(selectedAddress));

            const loaded = await loadRazorpayScript();
            if (!loaded) {
                toast.error("Failed to load Razorpay payment gateway. Please try again.");
                setLoading(false);
                return;
            }

            const options = {
                key: RAZORPAY_KEY,
                amount: totalAmount * 100, // in paise
                currency: "INR",
                name: "FasalShop",
                description: "Premium Purchase Order",
                image: "https://images.unsplash.com/photo-1598327105666-5b89351aff97?w=120&auto=format&fit=crop&q=80",
                handler: async function (response) {
                    setLoading(true);
                    try {
                        const payment_obj = {
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_order_id: response.razorpay_order_id || `order_${Date.now()}`,
                            razorpay_signature: response.razorpay_signature || `sig_${Date.now()}`
                        };

                        const formData = new FormData();
                        formData.append('user_id', userIdToLink);
                        formData.append('address_id', addrId);
                        formData.append('type', (items.length > 1 || !checkoutData) ? "2" : "1"); // 1 = buy_now, 2 = cart
                        formData.append('total_cost', String(totalAmount));
                        formData.append('vendor_id', vendorId);
                        formData.append('items', JSON.stringify(orderItems));
                        formData.append('payment_method', '1');
                        formData.append('discount', String(discount || 0));
                        formData.append('shipping_charge', String(deliveryCharge || 0));
                        formData.append('razorpay_payment_id', payment_obj.razorpay_payment_id);
                        formData.append('razorpay_order_id', payment_obj.razorpay_order_id);
                        formData.append('razorpay_signature', payment_obj.razorpay_signature);
                        formData.append('payment_obj', JSON.stringify(payment_obj));

                        console.log("Placing online order using FormData payload...");

                        const responseData = await service.post(SummaryApi.placeOrder.url, formData);

                        if (responseData.success === "true" || responseData.success === true) {
                            toast.success(responseData.message || "Payment successful & order placed! Thank you for shopping.");
                            const finalOrderId = responseData?.extraData?.order_id || responseData?.order_id || `FSL-${Math.floor(100000 + Math.random() * 900000)}`;
                            setOrderId(finalOrderId);
                            setStep(4);
                            trackLead({
                                content_name: 'Order Placement (Online)',
                                value: Number(totalAmount) || 0,
                                currency: 'INR',
                                order_id: finalOrderId
                            });
                            if (typeof fetchUserAddToCart === 'function') {
                                fetchUserAddToCart();
                            }
                        } else {
                            console.error("Order failed response:", responseData);
                            toast.error(responseData.message || "Failed to place order. Please try again.");
                        }
                    } catch (err) {
                        console.error("Error creating online order:", err);
                        toast.error(err?.data?.message || err?.message || "Failed to place order. Please try again.");
                    } finally {
                        setLoading(false);
                    }
                },
                prefill: {
                    name: user?.name || activeAddress?.name || 'User',
                    email: user?.email || 'user@example.com',
                    contact: user?.phone || activeAddress?.mobile || '1234567890'
                },
                theme: {
                    color: "#00b259"
                },
                modal: {
                    ondismiss: function () {
                        setLoading(false);
                    }
                }
            };

            const rzp = new window.Razorpay(options);
            rzp.open();
        } catch (error) {
            console.error("Place order exception:", error);
            toast.error(error?.data?.message || error?.message || "An unexpected error occurred");
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex justify-end overflow-hidden font-sans">
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity duration-300 animate-in fade-in"
                onClick={onClose}
            ></div>

            <div className="relative w-full max-w-[440px] bg-white h-full shadow-2xl flex flex-col transform transition-transform duration-500 animate-in slide-in-from-right-full">

                {/* Compact Header */}
                <div className="bg-[#143d2c] text-white px-6 py-8 relative overflow-hidden shrink-0">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-2xl -translate-y-8 translate-x-8 pointer-events-none"></div>
                    <div className="relative z-10 flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-black uppercase tracking-[2px] text-white">{t.checkout}</h2>
                            <p className="text-[#00b259] font-bold text-[10px] tracking-widest mt-1 uppercase">
                                {step === 4 ? t.done : t.stepCount.replace('%step%', step)}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center hover:bg-white/20 transition-all active:scale-90"
                        >
                            <MdClose size={24} />
                        </button>
                    </div>

                    {step < 4 && (
                        <div className="mt-6 flex gap-2">
                            {[1, 2, 3].map(i => (
                                <div key={i} className={`h-1 flex-1 rounded-full transition-all duration-500 ${i <= step ? 'bg-[#00b259]' : 'bg-white/10'}`}></div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Main Content Area */}
                <div className="flex-1 overflow-y-auto px-5 py-6 custom-scrollbar bg-[#f8f9f8]">

                    {step === 1 && (
                        <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="bg-[#00b259] text-white text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-wider">{t.reviewOrder}</span>
                            </div>

                            {items.length === 0 ? (
                                <div className="bg-white rounded-3xl p-8 border border-gray-100 text-center flex flex-col items-center justify-center space-y-4 shadow-sm my-4">
                                    <div className="w-16 h-16 rounded-full bg-emerald-50 text-[#00b259] flex items-center justify-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h4 className="font-black text-[#143d2c] text-base uppercase">{t.emptyCart || "Your Cart is Empty"}</h4>
                                        <p className="text-gray-400 text-xs mt-1">{t.noItemsInCart || "There are no items in your checkout order."}</p>
                                    </div>
                                    <button
                                        onClick={onClose}
                                        className="px-6 py-2.5 bg-[#143d2c] text-white text-xs font-black rounded-xl uppercase tracking-wider hover:bg-[#00b259] transition-colors"
                                    >
                                        {t.continueShopping || "Continue Shopping"}
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {(translatedItems.length === items.length ? translatedItems : items).map((item, idx) => {
                                        const p = (typeof item.productId === 'object' ? item.productId : null) || item.product || {};
                                        const rate = item.selectedRate || (item.item_rate && item.item_rate[0]) || (item.item_rates && item.item_rates[0]) || {};

                                        // Better image resolution
                                        let img = item.image || rate.image || p.productImage?.[0] || p.image?.[0] || "";
                                        if (typeof img === 'string' && img && !img.startsWith('http')) {
                                            img = `https://fasalshop.com/${img.startsWith('/') ? img.substring(1) : img}`;
                                        } else if (!img || img === "https://www.fasalshop.com/product-images/") {
                                            img = "https://placehold.co/400x400/eaeff6/2a4b7c?text=FasalShop";
                                        }

                                        const availableRates = (Array.isArray(p.item_rates) && p.item_rates.length > 0 ? p.item_rates : (Array.isArray(p.item_rate) && p.item_rate.length > 0 ? p.item_rate : (p.rates || []))) ||
                                            (item.item_rates || item.item_rate || item.rates || []);
                                        const currentPrice = getProductSellingPrice(item);
                                        const pName = item.translatedName || item.itemname || rate.itemname || p.itemname || p.productName || t.product;

                                        return (
                                            <div key={idx} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex flex-col gap-3 group relative overflow-hidden">
                                                <div className="flex gap-4">
                                                    <div className="w-16 h-16 bg-gray-50 rounded-xl p-1.5 flex items-center justify-center border border-gray-50 shrink-0">
                                                        <img src={img} className="max-w-full max-h-full object-contain mix-blend-multiply" alt="" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className='flex justify-between items-start'>
                                                            <h4 className="text-[#143d2c] font-bold text-sm truncate uppercase tracking-tight">{pName}</h4>
                                                            <button
                                                                onClick={() => deleteItem(idx)}
                                                                title="Remove item"
                                                                className='text-gray-400 hover:text-red-500 hover:bg-red-50 p-1.5 rounded-lg transition-all'
                                                            >
                                                                <MdClose size={18} />
                                                            </button>
                                                        </div>
                                                        <p className="text-[#00b259] text-[9px] font-black uppercase tracking-widest mt-0.5">{item.translatedSub || p.subcategory_name || p.category_name || t.harvest}</p>

                                                        <div className="flex items-center justify-between mt-2">
                                                            <span className="text-base font-black text-[#143d2c]">{displayINRCurrency(currentPrice)}</span>
                                                            <div className="flex items-center bg-gray-50 rounded-lg p-0.5 border border-gray-100 shadow-inner scale-90">
                                                                <button
                                                                    onClick={() => updateItemQuantity(idx, -1)}
                                                                    title={Number(item.quantity || item.qty || 1) === 1 ? "Remove item" : "Decrease quantity"}
                                                                    className="w-7 h-7 flex items-center justify-center text-[#143d2c] hover:bg-white rounded-md transition-all font-bold text-lg leading-none"
                                                                >
                                                                    -
                                                                </button>
                                                                <span className="w-7 text-center font-black text-xs">{item.quantity || item.qty || 1}</span>
                                                                <button
                                                                    onClick={() => updateItemQuantity(idx, 1)}
                                                                    title="Increase quantity"
                                                                    className="w-7 h-7 flex items-center justify-center text-[#00b259] hover:bg-white rounded-md transition-all font-bold text-lg leading-none"
                                                                >
                                                                    +
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {availableRates.length > 1 && (
                                                    <div className="pt-3 border-t border-gray-50 flex flex-wrap gap-1.5">
                                                        {availableRates.map((r) => (
                                                            <button
                                                                key={r.id || r._id}
                                                                onClick={() => updateItemRate(idx, r)}
                                                                className={`px-3 py-1.5 rounded-lg text-[10px] font-black transition-all border-2 ${String(item.selectedRate?.id || item.selectedRate?._id) === String(r.id || r._id) ? 'bg-[#00b259] text-white border-[#00b259]' : 'bg-gray-50 text-gray-400 border-transparent'}`}
                                                            >
                                                                {r.qty} {r.unit_name || r.uom || r.unit}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Summary Detail Card */}
                            <div className="bg-[#143d2c] rounded-3xl p-6 text-white shadow-xl relative overflow-hidden">
                                <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/5 rounded-full blur-2xl"></div>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center opacity-60 text-[11px] font-bold uppercase tracking-wider">
                                        <span>{t.itemsTotal}</span>
                                        <span>{displayINRCurrency(mrpTotal)}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-[#00b259] text-[11px] font-bold uppercase tracking-wider">
                                        <span>{t.savings}</span>
                                        <span>- {displayINRCurrency(discount)}</span>
                                    </div>
                                    <div className="flex justify-between items-center opacity-60 text-[11px] font-bold uppercase tracking-wider">
                                        <span>{t.shipping}</span>
                                        <span className={deliveryCharge === 0 ? 'text-[#00b259]' : ''}>{deliveryCharge === 0 ? t.free : displayINRCurrency(deliveryCharge)}</span>
                                    </div>
                                    <div className="pt-4 border-t border-white/10 flex justify-between items-center">
                                        <span className="text-lg font-black uppercase tracking-tighter">{t.amountPayable}</span>
                                        <span className="text-2xl font-black text-[#00b259] tracking-tighter">{displayINRCurrency(totalAmount)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white border border-gray-100 rounded-2xl p-4 flex items-center gap-3">
                                <MdVerifiedUser className="text-[#00b259]" size={24} />
                                <p className="text-[10px] font-bold text-[#143d2c] leading-tight flex-1">
                                    {t.qualityAssured}
                                </p>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="bg-[#00b259] text-white text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-wider">{t.selectLocation}</span>
                            </div>

                            <div className="space-y-3">
                                {!showAddAddress ? (
                                    <>
                                        {addresses.map(addr => {
                                            const addrId = addr.address_id || addr.id || addr._id;
                                            const isSelected = String(selectedAddress) === String(addrId);

                                            const safe = (val1, val2, val3, fallback) => {
                                                const values = [val1, val2, val3];
                                                for (let v of values) {
                                                    if (typeof v === 'string' || typeof v === 'number') return String(v).trim();
                                                }
                                                return fallback;
                                            };

                                            return (
                                                <div
                                                    key={addrId}
                                                    onClick={() => handleSelectAddress(addrId)}
                                                    className={`p-4 rounded-2xl border-2 transition-all cursor-pointer bg-white group relative overflow-hidden ${isSelected ? 'border-[#00b259] shadow-md bg-[#f0f9f2]/30' : 'border-gray-100 hover:border-gray-200'}`}
                                                >
                                                    {isSelected && (
                                                        <div className='absolute top-0 right-0 bg-[#00b259] text-white text-[8px] font-black px-2.5 py-1 rounded-bl-xl uppercase tracking-widest'>{t.selected}</div>
                                                    )}

                                                    <div className="flex items-start gap-4">
                                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-sm ${isSelected ? 'bg-[#00b259] text-white' : 'bg-gray-50 text-gray-400'}`}>
                                                            <MdLocationOn size={20} />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <h4 className="font-black text-[#143d2c] text-sm leading-tight uppercase truncate">
                                                                    {safe(addr.name, addr.full_name, addr.customer_name, t.noName)}
                                                                </h4>
                                                                <span className="bg-gray-100 text-gray-500 text-[7px] font-black px-1.5 py-0.5 rounded-md uppercase tracking-wider">{safe(addr.type, addr.address_type, '', t.home)}</span>
                                                            </div>

                                                            <p className="text-gray-600 font-bold text-[12px] leading-snug">
                                                                {safe(addr.address, addr.address_line_1, addr.location, t.noAddress)}
                                                            </p>

                                                            {addr.landmark && (
                                                                <p className="text-[10px] text-[#00b259] font-black mt-1 inline-flex items-center gap-1 bg-[#00b259]/5 px-1.5 py-0.5 rounded-md">
                                                                    📍 <span className='opacity-70 font-bold'>{t.near}:</span> {safe(addr.landmark, '', '', '')}
                                                                </p>
                                                            )}

                                                            <p className="text-[#143d2c] text-[12px] font-black mt-1.5">
                                                                {safe(addr.city, '', '', t.city)}, {safe(addr.state, '', '', t.state)} - <span className="text-[#00b259] border-b border-[#00b259]/20 pb-0.5">{safe(addr.pin, addr.pincode, addr.pin_code, '000000')}</span>
                                                            </p>

                                                            <div className="mt-3 flex items-center gap-2 text-[#143d2c] font-black text-[12px] bg-gray-50/80 w-full px-3 py-1.5 rounded-xl border border-gray-100">
                                                                <span className="text-xs">📞</span> {safe(addr.mobile, addr.phone, addr.phone_number, t.noPhone)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        <button
                                            onClick={() => setShowAddAddress(true)}
                                            className="w-full py-4 rounded-2xl border-2 border-dashed border-gray-200 text-gray-400 font-black text-[10px] uppercase tracking-widest hover:border-[#00b259] hover:text-[#00b259] transition-all flex items-center justify-center gap-2"
                                        >
                                            <FaPlus size={10} /> {t.addNewAddress}
                                        </button>
                                    </>
                                ) : (
                                    <form onSubmit={handleAddAddress} className="bg-white p-5 rounded-[2.5rem] border border-gray-100 shadow-sm space-y-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-[10px] font-black text-[#00b259] uppercase tracking-[3px]">{t.addNewAddress}</p>
                                            <button type="button" onClick={() => setShowAddAddress(false)} className="text-gray-400 hover:text-red-500"><MdClose size={20} /></button>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3">
                                            <input
                                                required
                                                type="text"
                                                placeholder={t.name}
                                                className="w-full bg-gray-50 border-2 border-transparent focus:border-[#00b259]/30 rounded-xl px-4 py-2.5 text-xs font-bold"
                                                value={newAddress.name}
                                                onChange={(e) => setNewAddress({ ...newAddress, name: e.target.value })}
                                            />
                                            <input
                                                required
                                                type="text"
                                                maxLength={10}
                                                placeholder={t.phone}
                                                className="w-full bg-gray-50 border-2 border-transparent focus:border-[#00b259]/30 rounded-xl px-4 py-2.5 text-xs font-bold"
                                                value={newAddress.phone}
                                                onChange={(e) => {
                                                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                                    setNewAddress({ ...newAddress, phone: val });
                                                }}
                                            />
                                        </div>
                                        <textarea
                                            required
                                            placeholder={t.completeAddress}
                                            className="w-full bg-gray-50 border-2 border-transparent focus:border-[#00b259]/30 rounded-xl px-4 py-2.5 text-xs font-bold min-h-[80px]"
                                            value={newAddress.address}
                                            onChange={(e) => setNewAddress({ ...newAddress, address: e.target.value })}
                                        ></textarea>
                                        <div className="grid grid-cols-2 gap-3">
                                            <input
                                                required
                                                type="text"
                                                placeholder={t.city}
                                                className="w-full bg-gray-50 border-2 border-transparent focus:border-[#00b259]/30 rounded-xl px-4 py-2.5 text-xs font-bold"
                                                value={newAddress.city}
                                                onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                                            />
                                            <input
                                                required
                                                type="text"
                                                maxLength={6}
                                                placeholder={t.pincode}
                                                className="w-full bg-gray-50 border-2 border-transparent focus:border-[#00b259]/30 rounded-xl px-4 py-2.5 text-xs font-bold"
                                                value={newAddress.pincode}
                                                onChange={(e) => {
                                                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                                    setNewAddress({ ...newAddress, pincode: val });
                                                }}
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <input
                                                required
                                                type="text"
                                                placeholder={t.state}
                                                className="w-full bg-gray-50 border-2 border-transparent focus:border-[#00b259]/30 rounded-xl px-4 py-2.5 text-xs font-bold"
                                                value={newAddress.state}
                                                onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })}
                                            />
                                            <input
                                                type="text"
                                                placeholder={t.landmark}
                                                className="w-full bg-gray-50 border-2 border-transparent focus:border-[#00b259]/30 rounded-xl px-4 py-2.5 text-xs font-bold"
                                                value={newAddress.landmark}
                                                onChange={(e) => setNewAddress({ ...newAddress, landmark: e.target.value })}
                                            />
                                        </div>
                                        <div className="flex gap-2">
                                            {[t.home, t.farm, t.office].map(type => (
                                                <button
                                                    key={type}
                                                    type="button"
                                                    onClick={() => setNewAddress({ ...newAddress, type })}
                                                    className={`flex-1 py-2 rounded-xl text-[10px] font-black border-2 transition-all ${newAddress.type === type ? 'bg-[#00b259] text-white border-[#00b259]' : 'bg-gray-50 text-gray-400 border-transparent'}`}
                                                >
                                                    {type}
                                                </button>
                                            ))}
                                        </div>
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full bg-[#143d2c] text-white font-black py-3.5 rounded-xl text-[10px] uppercase tracking-[3px] shadow-lg"
                                        >
                                            {loading ? t.adding : t.saveAddress}
                                        </button>
                                    </form>
                                )}
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="bg-[#00b259] text-white text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-wider">{t.selectPaymentMethod}</span>
                            </div>

                            <div className="space-y-3">
                                {/* Online Payment Option */}
                                <div
                                    onClick={() => setPaymentMethod('online')}
                                    className={`p-5 rounded-2xl border-2 transition-all cursor-pointer bg-white group relative overflow-hidden flex items-center justify-between ${paymentMethod === 'online' ? 'border-[#00b259] shadow-md bg-[#f0f9f2]/30' : 'border-gray-100 hover:border-gray-200'}`}
                                >
                                    {paymentMethod === 'online' && (
                                        <div className='absolute top-0 right-0 bg-[#00b259] text-white text-[8px] font-black px-2.5 py-1 rounded-bl-xl uppercase tracking-widest'>{t.selected}</div>
                                    )}
                                    <div className="flex items-center gap-4">
                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-sm ${paymentMethod === 'online' ? 'bg-[#00b259] text-white' : 'bg-gray-50 text-gray-400'}`}>
                                            <MdPayment size={24} />
                                        </div>
                                        <div>
                                            <h4 className="font-black text-[#143d2c] text-sm uppercase">{t.onlinePayment}</h4>
                                            <p className="text-gray-400 text-xs font-bold mt-0.5">{t.onlineDesc}</p>
                                        </div>
                                    </div>
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'online' ? 'border-[#00b259] bg-[#00b259]' : 'border-gray-300'}`}>
                                        {paymentMethod === 'online' && <div className="w-2 h-2 rounded-full bg-white"></div>}
                                    </div>
                                </div>

                                {/* Cash on Delivery Option */}
                                <div
                                    onClick={() => setPaymentMethod('cod')}
                                    className={`p-5 rounded-2xl border-2 transition-all cursor-pointer bg-white group relative overflow-hidden flex items-center justify-between ${paymentMethod === 'cod' ? 'border-[#00b259] shadow-md bg-[#f0f9f2]/30' : 'border-gray-100 hover:border-gray-200'}`}
                                >
                                    {paymentMethod === 'cod' && (
                                        <div className='absolute top-0 right-0 bg-[#00b259] text-white text-[8px] font-black px-2.5 py-1 rounded-bl-xl uppercase tracking-widest'>{t.selected}</div>
                                    )}
                                    <div className="flex items-center gap-4">
                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-sm ${paymentMethod === 'cod' ? 'bg-[#00b259] text-white' : 'bg-gray-50 text-gray-400'}`}>
                                            <FaTruck size={22} />
                                        </div>
                                        <div>
                                            <h4 className="font-black text-[#143d2c] text-sm uppercase">{t.codPayment}</h4>
                                            <p className="text-gray-400 text-xs font-bold mt-0.5">{t.codDesc}</p>
                                        </div>
                                    </div>
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'cod' ? 'border-[#00b259] bg-[#00b259]' : 'border-gray-300'}`}>
                                        {paymentMethod === 'cod' && <div className="w-2 h-2 rounded-full bg-white"></div>}
                                    </div>
                                </div>
                            </div>

                            {/* Summary Card */}
                            <div className="bg-[#143d2c] rounded-3xl p-6 text-white shadow-xl relative overflow-hidden mt-6">
                                <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/5 rounded-full blur-2xl"></div>
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center opacity-70 text-xs font-bold uppercase tracking-wider">
                                        <span>Total Items ({items.length})</span>
                                        <span>{displayINRCurrency(subtotal)}</span>
                                    </div>
                                    <div className="flex justify-between items-center opacity-70 text-xs font-bold uppercase tracking-wider">
                                        <span>Shipping Charge</span>
                                        <span className={deliveryCharge === 0 ? 'text-[#00b259]' : ''}>{deliveryCharge === 0 ? t.free : displayINRCurrency(deliveryCharge)}</span>
                                    </div>
                                    <div className="pt-3 border-t border-white/10 flex justify-between items-center">
                                        <span className="text-base font-black uppercase tracking-tighter">{t.amountPayable}</span>
                                        <span className="text-2xl font-black text-[#00b259] tracking-tighter">{displayINRCurrency(totalAmount)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 4 && (
                        <div className="h-full flex flex-col items-center justify-center text-center p-6 animate-in zoom-in-95 duration-500">
                            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center text-white shadow-2xl animate-bounce">
                                <MdCheckCircle size={50} />
                            </div>
                            <h2 className="text-2xl font-black text-[#143d2c] mt-8 tracking-tighter uppercase italic">{t.orderConfirmed}</h2>
                            <p className="text-gray-400 text-xs font-bold mt-2">{t.harvestPrepared}</p>

                            <div className="mt-8 bg-white border border-gray-50 p-6 rounded-[32px] w-full shadow-lg">
                                <div className="flex justify-between text-left mb-4">
                                    <div><p className="text-[8px] font-black text-gray-300 uppercase tracking-widest">{t.orderId}</p><p className="font-black text-sm text-[#143d2c]">#{orderId}</p></div>
                                    <div className="text-right"><p className="text-[8px] font-black text-gray-300 uppercase tracking-widest">{t.arrivingBy}</p><p className="font-black text-sm text-[#00b259]">24 Oct, 2026</p></div>
                                </div>
                                <button onClick={onClose} className="w-full bg-[#143d2c] text-white font-black py-4 rounded-2xl text-[10px] uppercase tracking-[3px] hover:bg-[#00b259] transition-all">{t.done}</button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Compact Footer */}
                {step < 4 && (
                    <div className="p-5 bg-white border-t border-gray-100 flex gap-3 h-[90px] shrink-0">
                        {step > 1 && (
                            <button
                                onClick={() => setStep(s => s - 1)}
                                className="w-[60px] h-full rounded-2xl bg-gray-50 flex items-center justify-center text-[#143d2c] border border-gray-100"
                            >
                                <MdChevronRight size={24} className="rotate-180" />
                            </button>
                        )}
                        <button
                            onClick={() => {
                                if (step === 1) {
                                    if (items.length === 0) return;
                                    setStep(2);
                                } else if (step === 2) {
                                    if (!selectedAddress) {
                                        toast.error("Please select a delivery address");
                                        return;
                                    }
                                    setStep(3);
                                } else if (step === 3) {
                                    handlePlaceOrder();
                                }
                            }}
                            disabled={loading || (step === 1 && items.length === 0)}
                            className={`flex-1 bg-gradient-to-r from-[#143d2c] to-[#00b259] text-white font-black rounded-2xl shadow-lg transition-all active:scale-[0.98] uppercase tracking-[3px] text-xs flex items-center justify-center gap-2 ${loading || (step === 1 && items.length === 0) ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                        >
                            {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : (
                                <>
                                    {step === 1 && t.continue}
                                    {step === 2 && t.continueToPayment}
                                    {step === 3 && (paymentMethod === 'cod' ? t.confirmOrder : t.payAndPlaceOrder)}
                                    <MdChevronRight size={18} />
                                </>
                            )}
                        </button>
                    </div>
                )}
                {/* DRAWER OVERLAYS */}
                {showSavedAddresses && (
                    <SavedAddressDrawer
                        onClose={() => setShowSavedAddresses(false)}
                        onSelect={(id) => {
                            setSelectedAddress(id);
                            setShowSavedAddresses(false);
                            fetchAddresses();
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default CheckoutSlider;
